/**
 * Sellyx API client — Phase A Sprint 1.
 *
 * Same-origin (frontend + backend served from the same preview host via ingress).
 * Auth = HttpOnly cookie set by backend; we always send credentials.
 */
// Sprint 6.X (FINDING-017): fail loud if the env wasn't injected at build
// time. Empty-string fallback used to silently same-origin-call, which masked
// misconfigured deployments. Allow same-origin only in dev.
import { notifyUnauthorized } from "@/lib/authSession";

const BASE = process.env.REACT_APP_BACKEND_URL || (
  process.env.NODE_ENV === "development" ? "" : (() => {
    // eslint-disable-next-line no-console
    console.error("REACT_APP_BACKEND_URL is not set — API calls will fail.");
    return "";
  })()
);
const API = `${BASE}/api`;

const ACTIVITY_HEADER = "X-Sellix-Activity";
const ACTIVITY_BACKGROUND = "background";

function activityHeaders({ background = false } = {}) {
  if (background) return { [ACTIVITY_HEADER]: ACTIVITY_BACKGROUND };
  return {};
}

function formatValidationDetail(detail) {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (!Array.isArray(detail)) return null;
  const parts = detail.map((item) => {
    if (typeof item === "string") return item;
    if (!item || typeof item !== "object") return null;
    let text = item.msg || item.message;
    if (!text) return null;
    // Pydantic prefixes custom ValueError text with "Value error, "
    text = String(text).replace(/^Value error,\s*/i, "").trim();
    const field = Array.isArray(item.loc)
      ? item.loc.filter((x) => x !== "body").join(".")
      : "";
    // Prefer clean email validation copy without "email: ..." prefix noise
    if (field === "email" || /valid email/i.test(text)) return text;
    return field ? `${field}: ${text}` : text;
  }).filter(Boolean);
  return parts.length ? parts.join(" ") : null;
}

function mapPlainDetailMessage(detail) {
  if (typeof detail !== "string") return null;
  const lower = detail.toLowerCase();
  if (lower.includes("pairing required") || lower.includes("not approved")) {
    return (
      "Sellyx is not approved on the OpenClaw gateway yet. "
      + "Open the OpenClaw Control UI, approve the pending device for this workspace, "
      + "then retry WhatsApp pairing."
    );
  }
  // FastAPI's generic route-missing 404 — let formatNotFoundError supply context.
  if (lower === "not found") return null;
  return detail;
}

function formatNotFoundError(path, nested, data) {
  if (path === "/general" || path.startsWith("/general/")) {
    return "General settings API is unavailable. Restart the backend to load the latest routes.";
  }
  if (path.includes("/brain/custom-sources")) {
    return "Knowledge source API is unavailable (HTTP 404). Restart the backend: cd backend && python -m uvicorn server:app --reload --port 8001";
  }
  if (path.startsWith("/brain/")) {
    return "Brain API endpoint not found. Restart the backend to load the latest Brain routes.";
  }
  if (path.includes("/conversations/") && path.includes("/drafts/")) {
    if (nested?.code === "draft_not_found") {
      return nested.message || "Draft not found for this conversation.";
    }
    if (data?.detail === "Not Found" || nested?.code === "not_found") {
      return "Draft action API is unavailable. Restart the backend to load the latest routes: cd backend && python -m uvicorn server:app --reload --port 8001";
    }
  }
  return "The requested API endpoint was not found (HTTP 404).";
}

function formatAuthError(path, nested) {
  if (path.startsWith("/auth/login") && nested?.message) {
    return nested.message;
  }
  return "Sign in again to continue.";
}

function formatValidationError(path, status, detail) {
  const validation = formatValidationDetail(detail);
  if (!validation) return null;
  if (
    (path.startsWith("/auth/login") || path.startsWith("/auth/forgot-password"))
    && status === 422
    && /email/i.test(validation)
  ) {
    return "Please enter a valid email address.";
  }
  return validation;
}

const _DELIVERY_ERROR_CODES = new Set([
  "delivery_failed",
  "delivery_unavailable",
  "channel_disconnected",
  "outbound_disabled",
]);

function formatServerError(nested) {
  if (nested?.code === "draft_generation_failed" && nested?.message) {
    return nested.message;
  }
  if (_DELIVERY_ERROR_CODES.has(nested?.code)) {
    return nested.message
      || "Unable to resend the message at the moment. Please try again in a few moments.";
  }
  if (nested?.code === "internal_error") {
    return "Unable to complete that request right now. Please try again in a few moments.";
  }
  return "Server error — check backend logs and try again.";
}

function formatApiError(path, status, data) {
  const nested = data?.detail?.error || data?.error;
  if (nested?.message && typeof nested.message === "string" && nested.message !== `HTTP ${status}`) {
    return nested.message;
  }
  const validation = formatValidationError(path, status, data?.detail);
  if (validation) return validation;
  const plainDetail = mapPlainDetailMessage(data?.detail);
  if (plainDetail) return plainDetail;
  if (status === 404) return formatNotFoundError(path, nested, data);
  if (status === 401 || status === 403) return formatAuthError(path, nested);
  if (status >= 500) return formatServerError(nested);
  if (nested?.message && typeof nested.message === "string") return nested.message;
  return `Request failed (HTTP ${status})`;
}

/** Always returns a user-facing string — never `[object Object]`. */
export function apiErrorMessage(err, fallback = "Request failed") {
  const msg = err?.message;
  if (typeof msg === "string" && msg && msg !== "[object Object]") return msg;
  const validation = formatValidationDetail(err?.data?.detail);
  if (validation) return validation;
  const plain = mapPlainDetailMessage(err?.data?.detail);
  if (plain) return plain;
  return fallback;
}

function isNetworkError(err) {
  if (!err) return false;
  if (err.code === "network_error") return true;
  const msg = String(err.message || "").toLowerCase();
  return msg.includes("failed to fetch")
    || msg.includes("networkerror")
    || msg.includes("load failed");
}

function networkError(path) {
  const longRunningDraft = typeof path === "string"
    && path.includes("/drafts/regenerate");
  const e = new Error(
    longRunningDraft
      ? "Connection interrupted while generating the draft. Checking whether it completed…"
      : "Cannot reach the Sellyx API. The server may be restarting — wait a moment and try again.",
  );
  e.code = "network_error";
  e.path = path;
  return e;
}

async function call(path, { method = "GET", body, headers, background = false } = {}) {
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...activityHeaders({ background }),
        ...(headers || {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw networkError(path);
  }
  let data = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = { raw: text }; }
  }
  if (!res.ok) {
    const isPublicAuth = path.startsWith("/auth/reset-password")
      || path.startsWith("/auth/forgot-password")
      || path.startsWith("/auth/login")
      || path === "/auth/me"
      || path === "/auth/password-policy";
    if (res.status === 401 && !isPublicAuth && !background) notifyUnauthorized();
    const message = formatApiError(path, res.status, data);
    const err = data?.detail?.error || data?.error || { code: "unknown", message };
    const e = new Error(message);
    e.code = err.code;
    e.status = res.status;
    e.data = data;
    e.path = path;
    throw e;
  }
  return data;
}

async function uploadCall(path, formData, { method = "POST" } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    credentials: "include",
    body: formData,
  });
  let data = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = { raw: text }; }
  }
  if (!res.ok) {
    if (res.status === 401) notifyUnauthorized();
    const message = formatApiError(path, res.status, data);
    const err = data?.detail?.error || data?.error || { code: "unknown", message };
    const e = new Error(message);
    e.code = err.code;
    e.status = res.status;
    e.data = data;
    e.path = path;
    throw e;
  }
  return data;
}

function downloadUrl(path) {
  return `${API}${path}`;
}

/** Download a brain document with session cookies (avoids cross-origin window.open failures). */
export async function downloadBrainDocument(sourceId, documentId, filename) {
  const res = await fetch(downloadUrl(`/brain/custom-sources/${sourceId}/documents/${documentId}/download`), {
    credentials: "include",
  });
  if (!res.ok) {
    let data = null;
    try { data = await res.json(); } catch { /* ignore */ }
    if (res.status === 401) notifyUnauthorized();
    throw new Error(formatApiError(`/brain/custom-sources/${sourceId}/documents/${documentId}/download`, res.status, data));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Download the ABM List Excel import template. */
export async function downloadCustomLeadsTemplate() {
  const res = await fetch(downloadUrl("/signals/custom/template"), {
    credentials: "include",
  });
  if (!res.ok) {
    let data = null;
    try { data = await res.json(); } catch { /* ignore */ }
    if (res.status === 401) notifyUnauthorized();
    throw new Error(formatApiError("/signals/custom/template", res.status, data));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sellyx_custom_leads_template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function downloadAuthenticatedFile(path, filename) {
  const res = await fetch(downloadUrl(path), { credentials: "include" });
  if (!res.ok) {
    let data = null;
    try { data = await res.json(); } catch { /* ignore */ }
    if (res.status === 401) notifyUnauthorized();
    throw new Error(formatApiError(path, res.status, data));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function leadReportQueryParams(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value == null || value === "" || value === "all") return;
    params.set(key, String(value));
  });
  return params;
}

export const reportsApi = {
  listTypes: () => call("/reports"),
  listLeads: ({
    limit = 25,
    offset = 0,
    q,
    lead_type,
    status,
    signal_source,
    assigned_user_id,
    email_status,
    date_preset,
    date_from,
    date_to,
    sort = "qualification_date",
    order = "desc",
  } = {}) => {
    const params = leadReportQueryParams({
      limit,
      offset,
      q,
      lead_type,
      status,
      signal_source,
      assigned_user_id,
      email_status,
      date_preset,
      date_from,
      date_to,
      sort,
      order,
    });
    return call(`/reports/leads?${params.toString()}`);
  },
  getLead: (id) => call(`/reports/leads/${id}`),
  listUsers: () => call("/reports/leads/users"),
  downloadNotificationHtml: (enrollmentId) => downloadAuthenticatedFile(
    `/reports/leads/${enrollmentId}/notification.html`,
    "lead-notification.html",
  ),
  downloadNotificationPdf: (enrollmentId) => downloadAuthenticatedFile(
    `/reports/leads/${enrollmentId}/notification.pdf`,
    "lead-notification.pdf",
  ),
  exportExcel: (filters = {}) => {
    const params = leadReportQueryParams(filters);
    return downloadAuthenticatedFile(
      `/reports/leads/export.xlsx?${params.toString()}`,
      "lead-reports.xlsx",
    );
  },
  exportPdf: (filters = {}) => {
    const params = leadReportQueryParams(filters);
    return downloadAuthenticatedFile(
      `/reports/leads/export.pdf?${params.toString()}`,
      "lead-reports.pdf",
    );
  },
};

export const authApi = {
  register: (b) => call("/auth/register", { method: "POST", body: b }),
  passwordPolicy: () => call("/auth/password-policy"),
  login: (b) => call("/auth/login", { method: "POST", body: b }),
  mfaEnroll: () => call("/auth/mfa/enroll", { method: "POST" }),
  mfaVerifyEnroll: (code) => call("/auth/mfa/verify-enrollment", { method: "POST", body: { code } }),
  mfaChallenge: (challenge_id, code) => call("/auth/mfa/challenge", { method: "POST", body: { challenge_id, code } }),
  forgot: (email) => call("/auth/forgot-password", { method: "POST", body: { email } }),
  reset: (token, new_password) => call("/auth/reset-password", { method: "POST", body: { token, new_password } }),
  changePassword: (current_password, new_password) => call("/auth/change-password", {
    method: "POST",
    body: { current_password, new_password },
  }),
  logout: () => call("/auth/logout", { method: "POST" }),
  me: () => call("/auth/me"),
  deleteAccount: (password, confirmation) => call("/auth/account/delete", { method: "POST", body: { password, confirmation } }),
  cancelDeletion: (email, password) => call("/auth/account/cancel-deletion", { method: "POST", body: { email, password } }),
};

export const billingApi = {
  plan: () => call("/billing/plan"),
  checkoutSession: (plan) => call("/billing/checkout-session", { method: "POST", body: { plan } }),
  portalSession: (return_path = "/settings/billing") => call("/billing/portal-session", { method: "POST", body: { return_path } }),
  mockConfirm: (session_id) => call("/billing/mock-confirm", { method: "POST", body: { session_id } }),
};

export const teamApi = {
  listMembers: () => call("/team/members"),
  invite: (email, role) => call("/team/invites", { method: "POST", body: { email, role } }),
  revokeInvite: (invite_id) => call(`/team/invites/${invite_id}`, { method: "DELETE" }),
  changeRole: (user_id, role) => call(`/team/members/${user_id}`, { method: "PATCH", body: { role } }),
  removeMember: (user_id) => call(`/team/members/${user_id}`, { method: "DELETE" }),
  acceptInvite: (token, password, display_name) => call("/auth/accept-invite", {
    method: "POST", body: { token, password, display_name },
  }),
};

export const personaApi = {
  get: () => call("/persona"),
  update: (patch) => call("/persona", { method: "PATCH", body: patch }),
  uploadAvatar: (file) => {
    const form = new FormData();
    form.append("file", file);
    return uploadCall("/persona/avatar", form);
  },
  deleteAvatar: () => call("/persona/avatar", { method: "DELETE" }),
  /** Fetch persisted avatar as a blob URL (sends session cookies). */
  fetchAvatarObjectUrl: async (persona) => {
    if (!persona?.has_avatar) return null;
    const version = persona.avatar_updated_at || "1";
    const res = await fetch(`${API}/persona/avatar?v=${encodeURIComponent(version)}`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};

export const safetyApi = {
  get: () => call("/safety"),
  update: (patch) => call("/safety", { method: "PATCH", body: patch }),
};

export const generalSettingsApi = {
  get: () => call("/general"),
  update: (patch) => call("/general", { method: "PATCH", body: patch }),
};

export const routingApi = {
  get: () => call("/routing"),
  save: (rules) => call("/routing", { method: "PUT", body: { rules } }),
};

export const connectionsApi = {
  list: () => call("/connections"),
  create: (kind, channel_config = {}) => call("/connections", { method: "POST", body: { kind, channel_config } }),
  delete: (id) => call(`/connections/${id}`, { method: "DELETE" }),
};

// Sprint 5.B — Conversations + drafts
export const conversationsApi = {
  list: ({ limit = 50, offset = 0, channel, q, filter, background = false } = {}) => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (channel && channel !== "all") params.set("channel", channel);
    if (q) params.set("q", q);
    if (filter && filter !== "all") params.set("filter", filter);
    return call(`/conversations?${params.toString()}`, { background });
  },
  inboxSummary: () => call("/conversations/inbox-summary"),
  syncInbox: ({ channel } = {}) => {
    const params = new URLSearchParams();
    if (channel && channel !== "all") params.set("channel", channel);
    const q = params.toString();
    const query = q ? "?" + q : "";
    return call(`/conversations/sync-inbox${query}`, { method: "POST" });
  },
  get: (id, { background = false, markRead = true } = {}) => {
    const params = new URLSearchParams();
    if (!markRead) params.set("mark_read", "false");
    const q = params.toString();
    const query = q ? "?" + q : "";
    return call(`/conversations/${id}${query}`, { background });
  },
  markRead: (id, { background = false } = {}) =>
    call(`/conversations/${id}/read`, { method: "POST", background }),
  approveDraft: (cid, mid) =>
    call(`/conversations/${cid}/drafts/${mid}/approve`, { method: "POST" }),
  retryOutbound: (cid, mid) =>
    call(`/conversations/${cid}/messages/${mid}/retry`, { method: "POST" }),
  editDraft: (cid, mid, payload) =>
    call(`/conversations/${cid}/drafts/${mid}/edit`, {
      method: "POST",
      body: typeof payload === "string" ? { body_text: payload } : payload,
    }),
  createDraft: (cid, payload) =>
    call(`/conversations/${cid}/drafts`, { method: "POST", body: payload }),
  restoreDraft: (cid, mid) =>
    call(`/conversations/${cid}/drafts/${mid}/restore`, { method: "POST" }),
  discardDraft: (cid, mid) =>
    call(`/conversations/${cid}/drafts/${mid}/discard`, { method: "POST" }),
  regenerateDraft: (cid) =>
    call(`/conversations/${cid}/drafts/regenerate`, { method: "POST" }),
  draftGenerationStatus: (cid, { background = true } = {}) =>
    call(`/conversations/${cid}/drafts/generation`, { background }),
  sendMessage: (cid, body_text) =>
    call(`/conversations/${cid}/messages`, { method: "POST", body: { body_text } }),
  updateStatus: (id, status) =>
    call(`/conversations/${id}`, { method: "PATCH", body: { status } }),
};

// Sprint 5.B — Gmail OAuth start (frontend then redirects to returned auth_url)
export const oauthApi = {
  status: () => call("/oauth/google/status"),
  startGoogle: (returnTo) => {
    const q = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : "";
    return call(`/oauth/google/start${q}`);
  },
  redditStatus: () => call("/oauth/reddit/status"),
  startReddit: (returnTo) => {
    const q = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : "";
    return call(`/oauth/reddit/start${q}`);
  },
  metaStatus: () => call("/oauth/meta/status"),
  facebookStatus: () => call("/oauth/facebook/status"),
  instagramStatus: () => call("/oauth/instagram/status"),
  startMeta: (returnTo, surface = "facebook") => {
    const params = new URLSearchParams();
    if (returnTo) params.set("return_to", returnTo);
    if (surface) params.set("surface", surface);
    const q = params.toString();
    return call(`/oauth/meta/start${q ? `?${q}` : ""}`);
  },
  startFacebook: (returnTo) => {
    const q = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : "";
    return call(`/oauth/facebook/start${q}`);
  },
  startInstagram: (returnTo) => {
    const q = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : "";
    return call(`/oauth/instagram/start${q}`);
  },
  startGoogleDrive: (returnTo, addAccount = false) => {
    const params = new URLSearchParams();
    if (returnTo) params.set("return_to", returnTo);
    if (addAccount) params.set("add_account", "true");
    const q = params.toString() ? `?${params.toString()}` : "";
    return call(`/oauth/google-drive/start${q}`);
  },
};

export const cloudStorageApi = {
  googleDriveStatus: () => call("/storage/google-drive/status"),
  googleDriveBrowse: (folderId, pageToken, connectionId) => {
    const params = new URLSearchParams();
    if (connectionId) params.set("connection_id", connectionId);
    if (folderId) params.set("folder_id", folderId);
    if (pageToken) params.set("page_token", pageToken);
    const q = params.toString() ? `?${params.toString()}` : "";
    return call(`/storage/google-drive/browse${q}`);
  },
  googleDriveSearch: (q, pageToken, connectionId) => {
    const params = new URLSearchParams({ q });
    if (connectionId) params.set("connection_id", connectionId);
    if (pageToken) params.set("page_token", pageToken);
    return call(`/storage/google-drive/search?${params.toString()}`);
  },
  googleDriveImport: (body) =>
    call("/storage/google-drive/import", { method: "POST", body }),
  disconnectGoogleDrive: (connectionId) =>
    call(`/storage/google-drive/connection/${connectionId}`, { method: "DELETE" }),
};

// Sprint 6 — Channel pairing (WhatsApp + Telegram via OpenClaw)
export const channelsApi = {
  // Two names for the same call — `pair` matches the Sprint 6 brief surface;
  // `startPair` is the original name kept for back-compat with any earlier caller.
  pair: (id, body = {}) => call(`/connections/${id}/pair`, { method: "POST", body }),
  startPair: (id, body = {}) => call(`/connections/${id}/pair`, { method: "POST", body }),
  pairStatus: (id, { background = false, attemptId } = {}) => {
    const qs = attemptId ? `?attempt_id=${encodeURIComponent(attemptId)}` : "";
    return call(`/connections/${id}/pair/status${qs}`, { background });
  },
  configureLinkedIn: (id, body) =>
    call(`/connections/${id}/linkedin/configure`, { method: "POST", body }),
  testTelegram: (id, body) =>
    call(`/connections/${id}/telegram/test`, { method: "POST", body }),
  configureTelegram: (id, body) =>
    call(`/connections/${id}/telegram/configure`, { method: "POST", body }),
  syncTelegramOpenclaw: (id) =>
    call(`/connections/${id}/telegram/sync-openclaw`, { method: "POST" }),
  configureQuora: (id, body) =>
    call(`/connections/${id}/quora/configure`, { method: "POST", body }),
};

// Sprint 7 — Today (decision UX) + People (real contacts)
export const todayApi = {
  get: () => call("/today"),
};

// In-app notification center (inbound Gmail / WhatsApp / Telegram)
export const notificationsApi = {
  list: ({ limit = 50, background = true } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    return call(`/notifications?${params.toString()}`, { background });
  },
  unreadCount: ({ background = true } = {}) =>
    call("/notifications/unread-count", { background }),
  markRead: (id) => call(`/notifications/${id}/read`, { method: "POST" }),
  clear: (id) => call(`/notifications/${id}/clear`, { method: "POST" }),
  clearAll: () => call("/notifications/clear-all", { method: "POST" }),
};

export const outreachApi = {
  summary: () => call("/outreach/summary"),
  listCampaigns: () => call("/outreach/campaigns"),
  checkCampaignName: (name, { excludeId } = {}) => {
    const params = new URLSearchParams({ name });
    if (excludeId) params.set("excludeId", excludeId);
    return call(`/outreach/campaigns/check-name?${params.toString()}`);
  },
  checkSignalConflicts: (sources = [], excludeId = null) => {
    const params = new URLSearchParams();
    if (sources?.length) params.set("sources", sources.join(","));
    if (excludeId) params.set("exclude", excludeId);
    const qs = params.toString();
    return call("/outreach/campaigns/signal-conflicts" + (qs ? `?${qs}` : ""));
  },
  listCampaignTimezones: (extraIana) => {
    const qs = extraIana ? `?extra=${encodeURIComponent(extraIana)}` : "";
    return call(`/outreach/campaigns/timezones${qs}`);
  },
  lookupCampaignTimezone: (iana) => {
    const params = new URLSearchParams({ iana });
    return call(`/outreach/campaigns/timezones/lookup?${params.toString()}`);
  },
  getWizard: (id) => call(`/outreach/campaigns/${id}/wizard`),
  saveWizard: (body) => call("/outreach/campaigns/wizard", { method: "POST", body }),
  getCampaign: (id) => call(`/outreach/campaigns/${id}`),
  createCampaign: (body) => call("/outreach/campaigns", { method: "POST", body }),
  updateCampaign: (id, body) => call(`/outreach/campaigns/${id}`, { method: "PATCH", body }),
  saveWorkflow: (id, workflow) => call(`/outreach/campaigns/${id}/workflow`, { method: "PUT", body: { workflow } }),
  deleteCampaign: (id) => call(`/outreach/campaigns/${id}`, { method: "DELETE" }),
  duplicateCampaign: (id) => call(`/outreach/campaigns/${id}/duplicate`, { method: "POST" }),
  publishCampaign: (id) => call(`/outreach/campaigns/${id}/publish`, { method: "POST" }),
  pauseCampaign: (id) => call(`/outreach/campaigns/${id}/pause`, { method: "POST" }),
  resumeCampaign: (id) => call(`/outreach/campaigns/${id}/resume`, { method: "POST" }),
  cancelCampaign: (id) => call(`/outreach/campaigns/${id}/cancel`, { method: "POST" }),
  setCampaignSignals: (id, signalSources) =>
    call(`/outreach/campaigns/${id}/signals`, { method: "PUT", body: { signalSources } }),
  getCampaignSignalConflicts: (id) =>
    call(`/outreach/campaigns/${id}/signal-conflicts`),
  /** @deprecated use setCampaignSignals */
  setCampaignChannels: (id, channels) =>
    call(`/outreach/campaigns/${id}/channels`, { method: "PUT", body: { channels } }),
  /** @deprecated use getCampaignSignalConflicts */
  getCampaignChannelConflicts: (id) =>
    call(`/outreach/campaigns/${id}/channel-conflicts`),
  setCampaignDefault: (id) =>
    call(`/outreach/campaigns/${id}/set-default`, { method: "POST" }),
  clearCampaignDefault: (id) =>
    call(`/outreach/campaigns/${id}/clear-default`, { method: "POST" }),
  listSignalDefaults: () => call("/outreach/campaigns/defaults"),
  /** @deprecated use listSignalDefaults */
  listChannelDefaults: () => call("/outreach/campaigns/defaults"),
  testSend: (id, body) => call(`/outreach/campaigns/${id}/test-send`, { method: "POST", body }),
  enroll: (id, body) => call(`/outreach/campaigns/${id}/enroll`, { method: "POST", body }),
  enrollRncs: (id, body) => call(`/outreach/campaigns/${id}/enroll-rncs`, { method: "POST", body }),
  enrollFromRmc: (id, body) => call(`/outreach/campaigns/${id}/enroll-from-rmc`, { method: "POST", body }),
  resolveLiveRnc: (body) => call("/outreach/rncs/resolve-live", { method: "POST", body }),
  autoEnrollRnc: (body) => call("/outreach/rncs/auto-enroll", { method: "POST", body }),
  getRncJourney: (projectId) => call(`/outreach/rncs/${encodeURIComponent(projectId)}/journey`),
  listRncJourneys: (projectIds) => call("/outreach/rncs/journeys", {
    method: "POST",
    body: { project_ids: projectIds },
  }),
  getAbmLeadJourney: (leadId) => call(`/outreach/abm/leads/${encodeURIComponent(leadId)}/journey`),
  listAbmJourneys: (leadIds) => call("/outreach/abm/journeys", {
    method: "POST",
    body: { lead_ids: leadIds },
  }),
  listEnrollments: (id) => call(`/outreach/campaigns/${id}/enrollments`),
  setEnrollmentLeadStatus: (campaignId, enrollmentId, lead_status, comment = null) =>
    call(`/outreach/campaigns/${campaignId}/enrollments/${enrollmentId}/status`, {
      method: "POST",
      body: comment ? { lead_status, comment } : { lead_status },
    }),
  simulateEnrollmentReply: (campaignId, enrollmentId, body = {}) =>
    call(`/outreach/campaigns/${campaignId}/enrollments/${enrollmentId}/simulate-reply`, {
      method: "POST",
      body,
    }),
  exitEnrollment: (campaignId, enrollmentId) =>
    call(`/outreach/campaigns/${campaignId}/enrollments/${enrollmentId}/exit`, {
      method: "POST",
    }),
  enrollAbmLeads: (id, body) => call(`/outreach/campaigns/${id}/enroll-abm-leads`, { method: "POST", body }),
  listCampaignActivity: (id, { since, limit } = {}) => {
    const params = new URLSearchParams();
    if (since) params.set("since", since);
    if (limit) params.set("limit", String(limit));
    const qs = params.toString();
    return call(`/outreach/campaigns/${id}/activity${qs ? `?${qs}` : ""}`);
  },
  validateWorkflow: (id, workflow) => call(`/outreach/campaigns/${id}/validate-workflow`, {
    method: "POST",
    body: workflow ? { workflow } : undefined,
  }),
  listWorkflowVersions: (id) => call(`/outreach/campaigns/${id}/workflow-versions`),
  analytics: (id) => call(`/outreach/campaigns/${id}/analytics`),
  aiGenerate: (id, body) => call(`/outreach/campaigns/${id}/ai-generate`, { method: "POST", body }),
  aiDesignSequence: (id, body) => call(`/outreach/campaigns/${id}/ai-design-sequence`, { method: "POST", body }),
  aiDesignSequencePreview: (body) => call("/outreach/campaigns/ai-design-sequence", { method: "POST", body }),
  listEnrollmentRules: (id) => call(`/outreach/campaigns/${id}/enrollment-rules`),
  createEnrollmentRule: (id, body) => call(`/outreach/campaigns/${id}/enrollment-rules`, { method: "POST", body }),
  listTemplates: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const q = qs.toString();
    return call(q ? `/outreach/templates?${q}` : "/outreach/templates");
  },
  getTemplate: (id) => call(`/outreach/templates/${id}`),
  createTemplate: (body) => call("/outreach/templates", { method: "POST", body }),
  updateTemplate: (id, body) => call(`/outreach/templates/${id}`, { method: "PATCH", body }),
  deleteTemplate: (id) => call(`/outreach/templates/${id}`, { method: "DELETE" }),
  duplicateTemplate: (id) => call(`/outreach/templates/${id}/duplicate`, { method: "POST" }),
  exportTemplate: (id) => call(`/outreach/templates/${id}/export`),
  importTemplate: (body) => call("/outreach/templates/import", { method: "POST", body }),
};

export const usageApi = {
  get: (window = "week") => call(`/usage?window=${window}`),
};

export const signalsApi = {
  // signals feed
  list: ({ status = "new", source, minScore = 0, q, limit = 50, offset = 0 } = {}) => {
    const params = new URLSearchParams({ status, min_score: String(minScore),
      limit: String(limit), offset: String(offset) });
    if (source) params.set("source", source);
    if (q) params.set("q", q);
    return call(`/signals?${params.toString()}`);
  },
  get:     (id)             => call(`/signals/${id}`),
  action:  (id, kind, note) => call(`/signals/${id}/action`,  { method: "POST", body: { kind, note } }),
  dismiss: (id, reason)     => call(`/signals/${id}/dismiss`, { method: "POST", body: { reason } }),
  updateStatus: (id, status, note) =>
    call(`/signals/${id}/status`, { method: "PATCH", body: { status, note } }),
  activity: (id) => call(`/signals/${id}/activity`),
  convertToRnc: (id) => call(`/signals/${id}/convert_to_rnc`, { method: "POST" }),
  getPreferences: () => call("/signals/settings/preferences"),
  putPreferences: (body) => call("/signals/settings/preferences", { method: "PUT", body }),
  ingestWebsite: (body) => call("/signals/ingest/website", { method: "POST", body }),
  ingestGoogleAds: (body) => call("/signals/ingest/google_ads", { method: "POST", body }),
  // sources
  listSources:   ()           => call("/signals/sources"),
  listCatalog:   ()           => call("/signals/catalog"),
  createSource:  (body)       => call("/signals/sources",       { method: "POST",  body }),
  updateSource:  (id, body)   => call(`/signals/sources/${id}`, { method: "PATCH", body }),
  deleteSource:  (id)         => call(`/signals/sources/${id}`, { method: "DELETE" }),
  pauseSource:   (id)         => call(`/signals/sources/${id}/pause`,   { method: "POST" }),
  resumeSource:  (id)         => call(`/signals/sources/${id}/resume`,  { method: "POST" }),
  pollNow:       (id)         => call(`/signals/sources/${id}/poll_now`,{ method: "POST" }),
  // v2 interest
  listV2Interest: () => call("/signals/v2_interest"),
  voteV2:         (source) => call("/signals/v2_interest", { method: "POST", body: { source } }),
  // RNC construct projects (dedicated DB)
  listRmcProjects: ({ limit = 10, offset = 0, q, sort = "project_name", order = "asc" } = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      sort,
      order,
    });
    if (q) params.set("q", q);
    return call(`/rmc/projects?${params.toString()}`);
  },
  getRmcProject: (projectId) => call(`/rmc/projects/${encodeURIComponent(projectId)}`),
  // Custom lead import (Excel)
  listCustomLeads: ({ q, limit = 10, offset = 0 } = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (q) params.set("q", q);
    return call(`/signals/custom/leads?${params.toString()}`);
  },
  getCustomLead: (leadId) => call(`/signals/custom/leads/${encodeURIComponent(leadId)}`),
  importCustomLeads: (file) => {
    const form = new FormData();
    form.append("file", file);
    return uploadCall("/signals/custom/import", form);
  },
  deleteCustomLead: (leadId) => call(
    `/signals/custom/leads/${encodeURIComponent(leadId)}`,
    { method: "DELETE" },
  ),
};

export const peopleApi = {
  list: ({
    limit = 25,
    offset = 0,
    q,
    channel,
    sort = "last_touch",
    order = "desc",
  } = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      sort,
      order,
    });
    if (q && String(q).trim()) params.set("q", String(q).trim());
    if (channel && channel !== "all") params.set("channel", channel);
    return call(`/people?${params.toString()}`);
  },
  get: (id) => call(`/people/${id}`),
};

export const healthApi = { check: () => call("/health") };

export const onboardingApi = {
  openclawStatus: () => call("/onboarding/openclaw/status"),
  provisionAgent: (body) => call("/onboarding/agent", { method: "POST", body }),
  status: () => call("/onboarding/status"),
  complete: () => call("/onboarding/complete", { method: "POST" }),
  updateProgress: (body) => call("/onboarding/progress", { method: "PATCH", body }),
  reset: () => call("/onboarding/reset", { method: "POST" }),
};

export const brainApi = {
  overview: () => call("/brain/overview"),
  documents: () => call("/brain/documents"),
  supportedFormats: () => call("/brain/supported-formats"),
  search: (body) => call("/brain/search", { method: "POST", body }),
  playgroundAnswer: (body) => call("/brain/playground/answer", { method: "POST", body }),
  listCustomSources: () => call("/brain/custom-sources"),
  createCustomSource: (body) => call("/brain/custom-sources", { method: "POST", body: { name: body.name, description: body.description || undefined } }),
  getCustomSource: (id) => call(`/brain/custom-sources/${id}`),
  renameCustomSource: (id, body) => call(`/brain/custom-sources/${id}`, { method: "PATCH", body }),
  deleteCustomSource: (id) => call(`/brain/custom-sources/${id}`, { method: "DELETE" }),
  assignCustomSource: (id, body) => call(`/brain/custom-sources/${id}/assignments`, { method: "PUT", body }),
  uploadCustomSource: (id, files) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return uploadCall(`/brain/custom-sources/${id}/upload`, form);
  },
  reprocessDocument: (sourceId, documentId) =>
    call(`/brain/custom-sources/${sourceId}/documents/${documentId}/reprocess`, { method: "POST" }),
  deleteDocument: (sourceId, documentId) =>
    call(`/brain/custom-sources/${sourceId}/documents/${documentId}`, { method: "DELETE" }),
  downloadDocumentUrl: (sourceId, documentId) =>
    downloadUrl(`/brain/custom-sources/${sourceId}/documents/${documentId}/download`),
  syncCustomSource: (id) => call(`/brain/custom-sources/${id}/sync`, { method: "POST" }),
  saveWizardSession: (id, body) =>
    call(`/brain/custom-sources/${id}/wizard-session`, { method: "PUT", body }),
  cancelWizardSession: (id) =>
    call(`/brain/custom-sources/${id}/wizard-session/cancel`, { method: "POST" }),
  listAgents: () => call("/brain/agents"),
};
